/* ======================================
   Maison d'Etto v2 — Cart
   V2Cart API helper + <cart-drawer> web component

   Contract (see docs/v2-conventions.md):
   - every <form action="/cart/add"> submit is intercepted
   - every [data-cart-add] button click is intercepted (ATC button listener)
   - every mutation goes through the Shopify Cart API (/cart/*.js) and asks
     for bundled section rendering of the drawer + icon bubble; the drawer
     body is fully re-rendered from that response after each action
   ====================================== */
(function () {
  'use strict';

  var SECTIONS = 'v2-cart-drawer,v2-cart-icon-bubble';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function jsonHeaders() {
    return { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  }

  async function handle(response, label) {
    if (!response.ok) {
      var err = {};
      try { err = await response.json(); } catch (e) { /* noop */ }
      var message = err.description || err.message || (label + ' failed: ' + response.status);
      var error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  var V2Cart = {
    /**
     * add(payload)
     *  - FormData from a product form
     *  - { id, quantity, properties }
     *  - { items: [{ id, quantity, properties }, ...] }
     */
    async add(payload) {
      var options;
      if (payload instanceof FormData) {
        payload.append('sections', SECTIONS);
        options = { method: 'POST', body: payload, headers: { 'Accept': 'application/json' } };
      } else {
        var body = payload && payload.items ? { items: payload.items } : {
          id: payload.id,
          quantity: payload.quantity || 1
        };
        if (!payload.items && payload.properties) body.properties = payload.properties;
        body.sections = SECTIONS;
        options = { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) };
      }
      return handle(await fetch(root + 'cart/add.js', options), 'Add');
    },

    async change(key, quantity) {
      return handle(await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ id: String(key), quantity: quantity, sections: SECTIONS })
      }), 'Change');
    },

    async update(updates) {
      return handle(await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(Object.assign({}, updates, { sections: SECTIONS }))
      }), 'Update');
    },

    async get() {
      return handle(await fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } }), 'Get');
    },

    open: function () { var d = document.querySelector('cart-drawer'); if (d) d.open(); },
    close: function () { var d = document.querySelector('cart-drawer'); if (d) d.close(); },
    toast: function (msg) { if (window.V2Toast) window.V2Toast(msg); }
  };
  window.V2Cart = V2Cart;

  /* ------------------------------------
     <cart-drawer>
     ------------------------------------ */
  class V2CartDrawerElement extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-cart-body]');
      this.panel = this.querySelector('[data-cart-panel]');
      this.debounceTimers = {};
      this.triggerElement = null;

      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onSubmit = this.handleFormSubmit.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('submit', this.onSubmit);
      /* capture phase so we win over the <a href="/cart"> navigation and
         so [data-cart-add] buttons anywhere on the page are caught */
      document.addEventListener('click', this.onDocumentClick, true);

      if (window.location.hash === '#cart') this.open();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('click', this.onDocumentClick, true);
    }

    /* ---------- add to cart: forms ---------- */
    handleFormSubmit(e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      e.preventDefault();
      var submitBtn = e.submitter || form.querySelector('[type="submit"]');
      this.addFromForm(form, submitBtn);
    }

    async addFromForm(form, button) {
      if (button && button.hasAttribute('disabled')) return;
      var title = this.titleFor(form);
      this.setLoading(button, true);
      try {
        var data = await V2Cart.add(new FormData(form));
        this.afterAdd(data, title);
      } catch (err) {
        this.fail(err);
      } finally {
        this.setLoading(button, false);
      }
    }

    /* ---------- add to cart: buttons + cart triggers ---------- */
    handleDocumentClick(e) {
      var trigger = e.target.closest('[data-cart-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
        return;
      }

      var btn = e.target.closest('[data-cart-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      this.addFromButton(btn);
    }

    async addFromButton(btn) {
      if (btn.hasAttribute('disabled') || btn.classList.contains('is-loading')) return;
      var payload;
      if (btn.dataset.items) {
        try { payload = { items: JSON.parse(btn.dataset.items) }; } catch (err) { this.fail(err); return; }
      } else {
        var variantId = btn.dataset.variantId;
        if (!variantId) { this.fail(new Error('Missing variant id')); return; }
        payload = { id: variantId, quantity: parseInt(btn.dataset.quantity || '1', 10) || 1 };
        if (btn.dataset.properties) {
          try { payload.properties = JSON.parse(btn.dataset.properties); } catch (err) { /* ignore bad json */ }
        }
      }
      var title = this.titleFor(btn);
      this.setLoading(btn, true);
      try {
        var data = await V2Cart.add(payload);
        this.afterAdd(data, title);
      } catch (err) {
        this.fail(err);
      } finally {
        this.setLoading(btn, false);
      }
    }

    titleFor(el) {
      var holder = el.closest('[data-product-title]');
      if (holder) return holder.dataset.productTitle;
      if (el.dataset && el.dataset.productTitle) return el.dataset.productTitle;
      return '';
    }

    setLoading(button, on) {
      if (!button) return;
      if (on) {
        button.dataset.originalText = button.dataset.originalText || button.textContent;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        if (button.dataset.loadingText) button.textContent = button.dataset.loadingText;
      } else {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        if (button.dataset.originalText) button.textContent = button.dataset.originalText;
      }
    }

    afterAdd(data, title) {
      this.renderSections(data.sections, 'add', title);
      var addedName = title || (data.items && data.items[0] && data.items[0].product_title) || data.product_title || 'Item';
      var template = this.getAttribute('data-added-message') || '{name} added to your bag';
      V2Cart.toast(template.replace('{name}', addedName));
      this.open();
    }

    fail(err) {
      console.error('[v2-cart]', err);
      V2Cart.toast(err && err.message ? err.message : (this.getAttribute('data-error-message') || 'Something went wrong. Please try again.'));
    }

    /* ---------- drawer interactions (delegated) ---------- */
    handleClick(e) {
      var target = e.target.closest('[data-action]');
      if (!target) return;
      var action = target.dataset.action;
      var key = target.dataset.key;

      switch (action) {
        case 'close':
          e.preventDefault();
          this.close();
          break;
        case 'decrease': {
          e.preventDefault();
          var down = Math.max(0, (parseInt(target.dataset.qty, 10) || 1) - 1);
          this.updateQtyDisplay(key, down);
          this.debouncedChange(key, down);
          break;
        }
        case 'increase': {
          e.preventDefault();
          var up = (parseInt(target.dataset.qty, 10) || 0) + 1;
          this.updateQtyDisplay(key, up);
          this.debouncedChange(key, up);
          break;
        }
        case 'remove':
          e.preventDefault();
          this.changeQuantity(key, 0);
          break;
      }
    }

    handleChange(e) {
      var input = e.target.closest('[data-quantity-input]');
      if (!input) return;
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.debouncedChange(input.dataset.key, qty);
    }

    handleKeydown(e) {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    }

    updateQtyDisplay(key, qty) {
      var input = this.querySelector('[data-quantity-input][data-key="' + key + '"]');
      if (input) input.value = qty;
      this.querySelectorAll('[data-action="increase"][data-key="' + key + '"], [data-action="decrease"][data-key="' + key + '"]').forEach(function (b) {
        b.dataset.qty = qty;
      });
    }

    debouncedChange(key, qty) {
      clearTimeout(this.debounceTimers[key]);
      this.debounceTimers[key] = setTimeout(() => this.changeQuantity(key, qty), 300);
    }

    async changeQuantity(key, qty) {
      var item = this.querySelector('[data-cart-item][data-key="' + key + '"]');
      if (item) item.classList.add('is-loading');
      var title = item ? item.dataset.productTitle : '';
      try {
        var data = await V2Cart.change(key, qty);
        this.renderSections(data.sections, qty === 0 ? 'remove' : 'update', title);
      } catch (err) {
        if (item) item.classList.remove('is-loading');
        this.fail(err);
      }
    }

    /* ---------- full re-render from bundled section rendering ---------- */
    renderSections(sections, action, title) {
      if (!sections) return;

      if (sections['v2-cart-drawer'] && this.body) {
        /* keep the #shopify-section-v2-cart-drawer wrapper: the section's
           CSS is scoped to it, and the initial server render has it too */
        this.body.innerHTML = sections['v2-cart-drawer'];
      }

      var count = null;
      if (sections['v2-cart-icon-bubble']) {
        var temp = document.createElement('div');
        temp.innerHTML = sections['v2-cart-icon-bubble'];
        var bubble = temp.querySelector('[data-cart-count]');
        count = bubble ? bubble.textContent.trim() : null;
      }
      if (count === null && this.body) {
        var inner = this.body.querySelector('[data-cart-item-count]');
        if (inner) count = inner.dataset.cartItemCount;
      }
      if (count !== null) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = count; });
        document.querySelectorAll('[data-cart-trigger]').forEach(function (el) {
          el.classList.toggle('has-items', parseInt(count, 10) > 0);
        });
      }

      this.announce(action, title);
      this.emit(action);
    }

    announce(action, title) {
      var status = this.querySelector('[data-cart-status]');
      if (!status) return;
      var messages = {
        add: title ? title + ' added to your bag' : 'Item added to your bag',
        remove: title ? title + ' removed from your bag' : 'Item removed from your bag',
        update: title ? 'Quantity updated for ' + title : 'Bag updated'
      };
      status.textContent = messages[action] || 'Bag updated';
    }

    emit(action) {
      V2Cart.get().then(function (cart) {
        document.dispatchEvent(new CustomEvent('v2:cart:updated', { detail: { cart: cart, action: action } }));
      }).catch(function () { /* noop */ });
    }

    /* ---------- open / close ---------- */
    open() {
      if (this.classList.contains('is-open')) return;
      this.triggerElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('v2-cart-open');
      requestAnimationFrame(() => {
        var closeBtn = this.querySelector('[data-action="close"]:not([data-cart-scrim])');
        if (closeBtn) closeBtn.focus();
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('v2-cart-open');
      if (window.location.hash === '#cart') history.replaceState(null, '', window.location.pathname + window.location.search);
      if (this.triggerElement && typeof this.triggerElement.focus === 'function') this.triggerElement.focus();
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', V2CartDrawerElement);
  }
})();
